import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { getPublicTicket, replyPublicTicket } from "../../api";
import { Loader2, RefreshCw, Send, CheckCircle2, Clock, AlertTriangle, MessageSquare, UserRound, Headset } from "lucide-react";

const ST_CLS = {
  nouveau: "bg-neutral-200 text-neutral-700", lu: "bg-sky-100 text-sky-800", repondu: "bg-brand/10 text-brand", assigne: "bg-indigo-100 text-indigo-800",
  en_cours: "bg-amber-100 text-amber-800", attente: "bg-orange-100 text-orange-800", clos: "bg-emerald-100 text-emerald-800", annule: "bg-neutral-200 text-neutral-500",
};
const fmt = (x) => {
  if (!x) return "";
  const d = /^\d+$/.test(String(x)) ? new Date(Number(x) * 1000) : new Date(x);
  return isNaN(d) ? String(x) : d.toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const Message = ({ m }) => (
  <div className={`flex gap-3 ${m.from_team ? "" : "flex-row-reverse"}`} data-testid={`ticket-msg-${m.id}`}>
    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${m.from_team ? "bg-brand text-white" : "bg-neutral-200 text-neutral-600"}`}>{m.from_team ? <Headset size={15} /> : <UserRound size={15} />}</div>
    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.from_team ? "bg-white border border-neutral-200" : "bg-brand text-white"}`}>
      <p className={`text-[11px] mb-1 ${m.from_team ? "text-neutral-500" : "text-white/80"}`}>{m.from_team ? (m.author || "Équipe PTITSERVICES-PRO") : (m.author || "Vous")} · {fmt(m.at)}{m.pending && " · en cours d'envoi"}</p>
      <p className="whitespace-pre-wrap break-words">{m.message}</p>
    </div>
  </div>
);

const TicketTracking = ({ ref, code }) => {
  const [t, setT] = useState(null);
  const [err, setErr] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");

  const load = useCallback(async () => {
    try { setT((await getPublicTicket(ref, code)).data); setErr(""); }
    catch (e) { setErr(e?.response?.data?.detail || "Ticket introuvable"); }
  }, [ref, code]);
  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault(); if (!reply.trim()) return;
    setBusy(true); setSent("");
    try { const { data } = await replyPublicTicket(ref, code, reply); setReply(""); setSent(data.synced ? "Message transmis à notre équipe ✓" : "Message enregistré, il sera transmis dans quelques minutes ✓"); await load(); }
    catch (ex) { setSent(ex?.response?.data?.detail || "Envoi impossible"); }
    finally { setBusy(false); }
  };

  if (err) return (
    <div className="bg-white border border-red-200 rounded-2xl p-8 text-center" data-testid="ticket-not-found">
      <AlertTriangle size={36} className="text-red-500 mx-auto mb-3" />
      <p className="font-semibold text-neutral-900">{err}</p>
      <p className="text-sm text-neutral-500 mt-1">Vérifiez la référence et le code d'accès indiqués dans votre email.</p>
      <Link to="/ticket" className="inline-block mt-4 text-brand font-semibold hover:underline">Retour</Link>
    </div>
  );
  if (!t) return <p className="text-neutral-500 flex items-center gap-2 justify-center py-16"><Loader2 className="animate-spin" size={18} /> Chargement du ticket…</p>;

  return (
    <div data-testid="ticket-tracking">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-brand mb-1">Suivi de ticket</p>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-neutral-900" data-testid="ticket-ref">{t.ref}{t.dolibarr_ref && <span className="text-base text-neutral-400 font-normal ml-2">({t.dolibarr_ref})</span>}</h1>
          <p className="text-neutral-600 mt-1">{t.subject}</p>
        </div>
        <button onClick={load} className="text-sm border border-neutral-300 rounded-md px-3 py-2 flex items-center gap-1.5 hover:bg-white" data-testid="ticket-refresh"><RefreshCw size={14} /> Actualiser</button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-2xl p-5 grid sm:grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-xs text-neutral-500 mb-1">Statut</p>
          <span className={`inline-flex items-center gap-1.5 font-semibold px-3 py-1 rounded-full ${ST_CLS[t.status.key] || ST_CLS.nouveau}`} data-testid="ticket-status">
            {t.status.closed ? <CheckCircle2 size={14} /> : <Clock size={14} />} {t.status.label}</span>
          {!t.synced && <p className="text-xs text-amber-700 mt-2" data-testid="ticket-pending-sync">Transmission à notre outil de gestion en cours…</p>}
        </div>
        <div className="space-y-1 text-neutral-700">
          <p><span className="text-neutral-500">Type :</span> {t.type_label} · <span className="text-neutral-500">Urgence :</span> {t.severity_label}</p>
          <p><span className="text-neutral-500">Déposé le :</span> {fmt(t.created_at)}</p>
          {t.societe && <p><span className="text-neutral-500">Société :</span> {t.societe}</p>}
          {t.assigned && <p><span className="text-neutral-500">Responsable :</span> {t.assigned}</p>}
        </div>
        <div className="sm:col-span-2 border-t border-neutral-100 pt-3">
          <p className="text-xs text-neutral-500 mb-1">Votre demande initiale</p>
          <p className="whitespace-pre-wrap text-neutral-800" data-testid="ticket-initial-message">{t.message}</p>
        </div>
      </div>

      <h2 className="text-base md:text-lg font-bold text-neutral-900 mt-8 mb-3 flex items-center gap-2"><MessageSquare size={18} className="text-brand" /> Échanges ({t.messages.length})</h2>
      <div className="space-y-3" data-testid="ticket-messages">
        {t.messages.length === 0 && <p className="text-sm text-neutral-500 bg-white border border-dashed border-neutral-300 rounded-xl p-4 text-center">Aucun échange pour le moment. Notre équipe vous répondra ici et par email.</p>}
        {t.messages.map((m) => <Message key={m.id} m={m} />)}
      </div>

      {!t.status.closed ? (
        <form onSubmit={send} className="mt-5 bg-white border border-neutral-200 rounded-2xl p-4" data-testid="ticket-reply-form">
          <label className="block text-xs font-semibold text-neutral-600 mb-1">Ajouter un message</label>
          <textarea rows={3} className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Précisions, réponse à notre équipe…" data-testid="ticket-reply-input" />
          <div className="flex items-center justify-between mt-2 gap-3">
            <p className="text-xs text-neutral-500" data-testid="ticket-reply-status">{sent}</p>
            <button disabled={busy || !reply.trim()} className="bg-brand hover:bg-brand-dark text-white font-semibold px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60" data-testid="ticket-reply-submit">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Envoyer</button>
          </div>
        </form>
      ) : <p className="mt-5 text-sm text-neutral-500 text-center" data-testid="ticket-closed-note">Ce ticket est clôturé. Pour une nouvelle demande, <Link to="/ticket" className="text-brand font-semibold hover:underline">déposez un nouveau ticket</Link>.</p>}
    </div>
  );
};

export default TicketTracking;
